import {
  IPayerAutoSelectionEvaluateRequest,
  IPayerAutoSelectionEvaluateResult,
  IPayerAutoSelectionRule,
  IPayerAutoSelectionRuleSaveRequest,
} from "types/PayerAutoSelection/PayerAutoSelection";

import { api } from ".";

/**
 * CLE-2845 — Payer Auto-Selection rules. SuperAdmin on every endpoint, reads included: a rule can
 * change the payer an estimate is priced under at every facility.
 *
 * The tag is registered here rather than in the shared tagTypes list, so this feature's cache stays
 * in its own file. Every write invalidates the list; the tester is a mutation (a POST that reads), so
 * it is never cached and always runs against the rules as saved.
 */
const apiWithPayerAutoSelectionTags = api.enhanceEndpoints({
  addTagTypes: ["PayerAutoSelectionRules"],
});

export const PAYER_AUTO_SELECTION_PATH =
  "eligibility/payer-auto-selection-rules";

export const payerAutoSelectionQueries =
  apiWithPayerAutoSelectionTags.injectEndpoints({
    endpoints: (builder) => ({
      getPayerAutoSelectionRules: builder.query<
        IPayerAutoSelectionRule[],
        void
      >({
        query: () => PAYER_AUTO_SELECTION_PATH,
        transformResponse: (response: { data: IPayerAutoSelectionRule[] }) =>
          response.data,
        providesTags: [{ type: "PayerAutoSelectionRules", id: "LIST" }],
      }),
      createPayerAutoSelectionRule: builder.mutation<
        IPayerAutoSelectionRule,
        IPayerAutoSelectionRuleSaveRequest
      >({
        query: (body) => ({
          url: PAYER_AUTO_SELECTION_PATH,
          method: "POST",
          body,
        }),
        transformResponse: (response: { data: IPayerAutoSelectionRule }) =>
          response.data,
        invalidatesTags: [{ type: "PayerAutoSelectionRules", id: "LIST" }],
      }),
      updatePayerAutoSelectionRule: builder.mutation<
        IPayerAutoSelectionRule,
        { id: string; body: IPayerAutoSelectionRuleSaveRequest }
      >({
        query: ({ id, body }) => ({
          url: `${PAYER_AUTO_SELECTION_PATH}/${encodeURIComponent(id)}`,
          method: "PUT",
          body,
        }),
        transformResponse: (response: { data: IPayerAutoSelectionRule }) =>
          response.data,
        invalidatesTags: [{ type: "PayerAutoSelectionRules", id: "LIST" }],
      }),
      deletePayerAutoSelectionRule: builder.mutation<{ id: string }, string>({
        query: (id) => ({
          url: `${PAYER_AUTO_SELECTION_PATH}/${encodeURIComponent(id)}`,
          method: "DELETE",
        }),
        transformResponse: (response: { data: { id: string } }) =>
          response.data,
        invalidatesTags: [{ type: "PayerAutoSelectionRules", id: "LIST" }],
      }),
      evaluatePayerAutoSelection: builder.mutation<
        IPayerAutoSelectionEvaluateResult,
        IPayerAutoSelectionEvaluateRequest
      >({
        query: (body) => ({
          url: `${PAYER_AUTO_SELECTION_PATH}/evaluate`,
          method: "POST",
          body,
        }),
        transformResponse: (response: {
          data: IPayerAutoSelectionEvaluateResult;
        }) => response.data,
      }),
    }),
  });

export const {
  useGetPayerAutoSelectionRulesQuery,
  useCreatePayerAutoSelectionRuleMutation,
  useUpdatePayerAutoSelectionRuleMutation,
  useDeletePayerAutoSelectionRuleMutation,
  useEvaluatePayerAutoSelectionMutation,
} = payerAutoSelectionQueries;
